import React from 'react';
import { Card, CardContent, Typography, Grid, Divider, Box, FormControlLabel, Switch, Tooltip } from '@mui/material';
import { WarningAmber as WarningIcon } from '@mui/icons-material';
import TemplateSelector from '../../../../components/templates/TemplateSelector';
import { BookingConfig } from '../../types';
import { NumberField } from '../Inputs/NumberField';
import { LocalTemplate, VerifyResult } from '../../../../types/templates';
import { getUnavailableTemplateIds } from '../../utils';

interface NotificationTemplatesFormProps {
  smbId: string;
  formData: Partial<BookingConfig & {
    smb_cancellation_alert_enabled?: boolean;
    smb_reschedule_alert_enabled?: boolean;
  }>;
  onChange: (field: string, value: any) => void;
  setActivePreviewField: React.Dispatch<React.SetStateAction<string | null>>;
  selectedTemplates: { fieldId: string; templateId: string | null }[];
  localTemplates: LocalTemplate[];
  templateVerifyErrors?: Record<string, VerifyResult>;
  collisions: Record<string, boolean>;
  errors: Record<string, string | null>;
}

export const NotificationTemplatesForm: React.FC<NotificationTemplatesFormProps> = ({
  smbId,
  formData,
  onChange,
  setActivePreviewField,
  selectedTemplates,
  localTemplates,
  templateVerifyErrors = {},
  collisions,
  errors
}) => {
  const smbCancellationEnabled = !!formData.smb_cancellation_alert_enabled;
  const smbRescheduleEnabled = !!formData.smb_reschedule_alert_enabled;

  const handleTemplateSelect = (field: string, tpl: LocalTemplate | null) => {
    onChange(field, tpl?.id || null);
    setActivePreviewField(field as string);
  };

  const handleToggleSmbCancellation = (enabled: boolean) => {
    onChange('smb_cancellation_alert_enabled', enabled);
    if (!enabled) {
      onChange('cancellation_notification_template_smb', null);
    }
  };

  const handleToggleSmbReschedule = (enabled: boolean) => {
    onChange('smb_reschedule_alert_enabled', enabled);
    if (!enabled) {
      onChange('reschedule_notification_template_smb', null);
    }
  };

  return (
    <Card sx={{ mb: 4, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
      <CardContent sx={{ p: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
          Notification Templates
        </Typography>
        <Typography color="textSecondary" sx={{ mb: 3 }}>
          Map your approved Meta templates to booking events. Mandatory templates are marked with an asterisk (*).
        </Typography>

        <Grid container rowSpacing={2} columnSpacing={4}>

          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5, color: 'text.primary' }}>
              Confirmations & New Bookings
            </Typography>
          </Grid>

          <Grid item xs={12} md={6} data-booking-error-key="confirmation_new_appointment_template_id">
            <TemplateSelector 
              smbId={smbId}
              templates={localTemplates}
              label="New Appointment Confirmation (To SMB)"
              value={formData.confirmation_new_appointment_template_id || ''}
              onSelect={(tpl) => handleTemplateSelect('confirmation_new_appointment_template_id', tpl)}
              unavailableTemplateIds={getUnavailableTemplateIds('confirmation_new_appointment_template_id', selectedTemplates)}
              error={!!errors.confirmation_new_appointment_template_id || collisions['confirmation_new_appointment_template_id']}
              helperText={errors.confirmation_new_appointment_template_id}
              templateVerifyError={templateVerifyErrors['confirmation_new_appointment_template_id']}
              onFocus={() => setActivePreviewField('confirmation_new_appointment_template_id')}
              required
            />
          </Grid>

          <Grid item xs={12}><Divider sx={{ opacity: 0.5 }} /></Grid>

          <Grid item xs={12} md={6} sx={{ mt: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5, color: 'text.primary' }}>
              Cancellations & Alerts
            </Typography>
          </Grid>

          <Grid item xs={12} md={6} sx={{ mt: 1 }}>
            <Box sx={{ ml: 0.8 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={smbCancellationEnabled}
                    onChange={(e) => handleToggleSmbCancellation(e.target.checked)}
                    size="small"
                  />
                }
                label={<Typography variant="body2" sx={{ fontWeight: 600 }}>Send Cancellation Alert to SMB</Typography>}
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={6} data-booking-error-key="cancellation_notification_template_lead">
            <TemplateSelector 
              smbId={smbId}
              templates={localTemplates}
              label="Cancellation Template (To Lead)"
              value={formData.cancellation_notification_template_lead || ''}
              onSelect={(tpl) => handleTemplateSelect('cancellation_notification_template_lead', tpl)}
              unavailableTemplateIds={getUnavailableTemplateIds('cancellation_notification_template_lead', selectedTemplates)}
              error={!!errors.cancellation_notification_template_lead || collisions['cancellation_notification_template_lead']}
              helperText={errors.cancellation_notification_template_lead}
              templateVerifyError={templateVerifyErrors['cancellation_notification_template_lead']}
              onFocus={() => setActivePreviewField('cancellation_notification_template_lead')}
              required
            />
          </Grid>

          <Grid item xs={12} md={6} data-booking-error-key="cancellation_notification_template_smb">
            <TemplateSelector 
              smbId={smbId}
              templates={localTemplates}
              label="Cancellation Template (To SMB)"
              disabled={!smbCancellationEnabled}
              value={formData.cancellation_notification_template_smb || ''}
              onSelect={(tpl) => handleTemplateSelect('cancellation_notification_template_smb', tpl)}
              unavailableTemplateIds={getUnavailableTemplateIds('cancellation_notification_template_smb', selectedTemplates)}
              error={!!errors.cancellation_notification_template_smb || collisions['cancellation_notification_template_smb']}
              helperText={errors.cancellation_notification_template_smb}
              templateVerifyError={templateVerifyErrors['cancellation_notification_template_smb']}
              onFocus={() => setActivePreviewField('cancellation_notification_template_smb')}
              required={smbCancellationEnabled}
            />
          </Grid>

          <Grid item xs={12}><Divider sx={{ opacity: 0.5 }} /></Grid>

          <Grid item xs={12} sx={{ mt: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5, color: 'text.primary' }}>
              Reminders
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 2 }}>
              <Box sx={{ ml: 0.8, mb: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!formData.allow_reminders_to_lead}
                      onChange={(e) => onChange('allow_reminders_to_lead', e.target.checked)}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2" sx={{ fontWeight: 600 }}>Send Reminders to Lead</Typography>}
                />
              </Box>
              <Box
                data-booking-error-key="reminder_duration_gap_lead"
                onClick={() => !formData.allow_reminders_to_lead}
                sx={{ mb: 2, cursor: !formData.allow_reminders_to_lead ? 'pointer' : 'default' }}
              >
                <NumberField
                  fullWidth
                  size="small"
                  label="Lead Reminder Schedule Time (mins before)"
                  disabled={!formData.allow_reminders_to_lead}
                  value={formData.reminder_duration_gap_lead ?? ''}
                  onChange={(val) => {
                    onChange('reminder_duration_gap_lead', val === '' ? null : parseInt(val));
                  }}
                  sx={{ opacity: !formData.allow_reminders_to_lead ? 0.6 : 1 }}
                  error={!!errors.reminder_duration_gap_lead || (
                    formData.allow_reminders_to_lead &&
                    formData.reminder_duration_gap_lead !== undefined && (
                      !formData.reminder_duration_gap_lead ||
                      formData.reminder_duration_gap_lead % 15 !== 0 ||
                      formData.reminder_duration_gap_lead < 15
                    )
                  )}
                  helperText={
                    (formData.allow_reminders_to_lead && (!formData.reminder_duration_gap_lead || formData.reminder_duration_gap_lead % 15 !== 0))
                      ? 'Must be a multiple of 15'
                      : (formData.allow_reminders_to_lead && formData.reminder_duration_gap_lead !== undefined && formData.reminder_duration_gap_lead !== null && formData.reminder_duration_gap_lead < 15)
                        ? 'Minimum 15 mins'
                        : errors.reminder_duration_gap_lead || (
                          (formData.allow_reminders_to_lead &&
                            formData.reminder_duration_gap_lead &&
                            formData.first_slot_offset &&
                            formData.reminder_duration_gap_lead > formData.first_slot_offset) ? (
                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: 'warning.main' }}>
                              <Tooltip title="This reminder is set further back than your first slot offset. Reminders for appointments booked immediately may be missed if the offset is smaller than the reminder time." arrow>
                                <WarningIcon sx={{ fontSize: 14 }} />
                              </Tooltip>
                              You can miss some reminders
                            </Box>
                          ) : 'Time before appointment to send reminder'
                        )
                  }
                  min={15}
                  step={15}
                />
              </Box>
              <Box data-booking-error-key="reminder_template_id_lead">
                <TemplateSelector 
                  smbId={smbId}
                  templates={localTemplates}
                  label="Lead Reminder Template"
                  disabled={!formData.allow_reminders_to_lead}
                  value={formData.reminder_template_id_lead || ''}
                  onSelect={(tpl) => handleTemplateSelect('reminder_template_id_lead', tpl)}
                  onFocus={() => setActivePreviewField('reminder_template_id_lead')}
                  unavailableTemplateIds={getUnavailableTemplateIds('reminder_template_id_lead', selectedTemplates)}
                  error={!!errors.reminder_template_id_lead || collisions['reminder_template_id_lead']}
                  helperText={errors.reminder_template_id_lead}
                  templateVerifyError={templateVerifyErrors['reminder_template_id_lead']}
                  required={!!formData.allow_reminders_to_lead}
                />
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 2 }}>
              <Box sx={{ ml: 0.8, mb: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!formData.allow_reminders_to_smb}
                      onChange={(e) => onChange('allow_reminders_to_smb', e.target.checked)}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2" sx={{ fontWeight: 600 }}>Send Reminders to SMB</Typography>}
                />
              </Box>
              <Box
                data-booking-error-key="reminder_duration_gap_smb"
                onClick={() => !formData.allow_reminders_to_smb}
                sx={{ mb: 2, cursor: !formData.allow_reminders_to_smb ? 'pointer' : 'default' }}
              >
                <NumberField
                  fullWidth
                  size="small"
                  label="SMB Reminder Schedule Time (mins before)"
                  disabled={!formData.allow_reminders_to_smb}
                  value={formData.reminder_duration_gap_smb ?? ''}
                  onChange={(val) => {
                    onChange('reminder_duration_gap_smb', val === '' ? null : parseInt(val));
                  }}
                  sx={{ opacity: !formData.allow_reminders_to_smb ? 0.6 : 1 }}
                  error={!!errors.reminder_duration_gap_smb || (
                    formData.allow_reminders_to_smb &&
                    formData.reminder_duration_gap_smb !== undefined && (
                      !formData.reminder_duration_gap_smb ||
                      formData.reminder_duration_gap_smb % 15 !== 0 ||
                      formData.reminder_duration_gap_smb < 15
                    )
                  )}
                  helperText={
                    (formData.allow_reminders_to_smb && (!formData.reminder_duration_gap_smb || formData.reminder_duration_gap_smb % 15 !== 0))
                      ? 'Must be a multiple of 15'
                      : (formData.allow_reminders_to_smb && formData.reminder_duration_gap_smb !== undefined && formData.reminder_duration_gap_smb !== null && formData.reminder_duration_gap_smb < 15)
                        ? 'Minimum 15 mins'
                        : errors.reminder_duration_gap_smb || (
                          (formData.allow_reminders_to_smb &&
                            formData.reminder_duration_gap_smb &&
                            formData.first_slot_offset &&
                            formData.reminder_duration_gap_smb > formData.first_slot_offset) ? (
                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: 'warning.main' }}>
                              <Tooltip title="This reminder is set further back than your first slot offset. Reminders for appointments booked immediately may be missed if the offset is smaller than the reminder time." arrow>
                                <WarningIcon sx={{ fontSize: 14 }} />
                              </Tooltip>
                              You can miss some reminders
                            </Box>
                          ) : 'Time before appointment to notify the business'
                        )
                  }
                  min={15}
                  step={15}
                />
              </Box>
              <Box data-booking-error-key="reminder_template_id_smb">
                <TemplateSelector 
                  smbId={smbId}
                  templates={localTemplates}
                  label="SMB Reminder Template"
                  disabled={!formData.allow_reminders_to_smb}
                  value={formData.reminder_template_id_smb || ''}
                  onSelect={(tpl) => handleTemplateSelect('reminder_template_id_smb', tpl)}
                  onFocus={() => setActivePreviewField('reminder_template_id_smb')}
                  unavailableTemplateIds={getUnavailableTemplateIds('reminder_template_id_smb', selectedTemplates)}
                  error={!!errors.reminder_template_id_smb || collisions['reminder_template_id_smb']}
                  helperText={errors.reminder_template_id_smb}
                  templateVerifyError={templateVerifyErrors['reminder_template_id_smb']}
                  required={!!formData.allow_reminders_to_smb}
                />
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12}><Divider sx={{ opacity: 0.5 }} /></Grid>

          <Grid item xs={12} md={6} sx={{ mt: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5, color: 'text.primary' }}>
              Rescheduling
            </Typography>
          </Grid>

          <Grid item xs={12} md={6} sx={{ mt: 1 }}>
            <Box sx={{ ml: 0.8 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={smbRescheduleEnabled}
                    onChange={(e) => handleToggleSmbReschedule(e.target.checked)}
                    size="small"
                  />
                }
                label={<Typography variant="body2" sx={{ fontWeight: 600 }}>Send Reschedule Alert to SMB</Typography>}
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={6} data-booking-error-key="reschedule_notification_template_lead">
            <TemplateSelector
              smbId={smbId}
              templates={localTemplates}
              label="Reschedule Template (To Lead)"
              value={formData.reschedule_notification_template_lead || ''}
              onSelect={(tpl) => handleTemplateSelect('reschedule_notification_template_lead', tpl)}
              unavailableTemplateIds={getUnavailableTemplateIds('reschedule_notification_template_lead', selectedTemplates)}
              error={!!errors.reschedule_notification_template_lead || collisions['reschedule_notification_template_lead']}
              helperText={errors.reschedule_notification_template_lead}
              templateVerifyError={templateVerifyErrors['reschedule_notification_template_lead']}
              onFocus={() => setActivePreviewField('reschedule_notification_template_lead')}
              required
            />
          </Grid>

          <Grid item xs={12} md={6} data-booking-error-key="reschedule_notification_template_smb">
            <TemplateSelector
              smbId={smbId}
              templates={localTemplates}
              label="Reschedule Template (To SMB)"
              disabled={!smbRescheduleEnabled}
              value={formData.reschedule_notification_template_smb || ''}
              onSelect={(tpl) => handleTemplateSelect('reschedule_notification_template_smb', tpl)}
              unavailableTemplateIds={getUnavailableTemplateIds('reschedule_notification_template_smb', selectedTemplates)}
              error={!!errors.reschedule_notification_template_smb || collisions['reschedule_notification_template_smb']}
              helperText={errors.reschedule_notification_template_smb}
              templateVerifyError={templateVerifyErrors['reschedule_notification_template_smb'] }
              onFocus={() => setActivePreviewField('reschedule_notification_template_smb')}
              required={smbRescheduleEnabled}
            />
          </Grid>
        </Grid>

        <Box sx={{
          mt: 4,
          p: 2,
          borderRadius: 2,
          bgcolor: 'rgba(0, 0, 0, 0.02)',
          border: '1px dashed',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5
        }}>
          <Typography variant="caption" color="textSecondary" sx={{ fontStyle: 'italic', lineHeight: 1.5 }}>
            <strong>Note:</strong> Meta (WhatsApp) templates are chargeable services. Sending these notifications may include additional messaging charges by Meta based on your template category and regional pricing.
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};
